import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import "./style.scss";
import OliverText, { TextType } from "@builder/components/general/OliverText";
import FormInput from "@builder/components/general/FormInput";
import { useAppDispatch, useAppSelector } from "@builder/store/store";
import { useOutsideClick } from "@builder/hooks/useOutsideClick";
import { v4 } from "uuid";
import FormImageInput from "@builder/components/general/FormImageInput";
import { useUploadAttachmentsMutation } from "@builder/store/slices/attachmentsApiSlice/attachmentsApiSlice";
import {
    addBuildSettingsKey,
    BuildSettingsKey,
    deleteBuildSettingsKey,
    NewBuildSettingKey,
    updateBuildSettingsKey
} from "@builder/store/slices/buildSettingsKeys/buildSettingsKeySlice";
import { removeBuildSettingKey } from "@builder/store/slices/BuilderSlice/builderSlice";
import { useTranslation } from "react-i18next";
import { useUploadImageMutation } from "@builder/store/slices/buildSettingsKeys/buildSettingsKeyApi";
import store from "@builder/store/store";
import { attachmentsApi } from "@builder/store/slices/attachmentsApiSlice/attachmentsApiSlice";

export const uploadFile = async (file: File, companyId: string) => {
    const result = await store.dispatch(attachmentsApi.endpoints.uploadAttachments.initiate({ companyId, files: [file] })).unwrap();
    return result?.[0]?.url || "";
};

interface CreateTextSettingFormProps {
    defaultValues?: Partial<BuildSettingsKey>;
    id?: string;
    closeEdit: () => void;
}

interface CreateImageForm extends BuildSettingsKey {
    fileList?: File[];
}

// Removed standalone uploadFile; rely on RTK slice instead
/*export const uploadFile = async (...args:any)=>{}*/

const CreateImageSettingForm = ({ defaultValues, closeEdit }: CreateTextSettingFormProps) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const build = useAppSelector(state => state.builder.build);
    const [deleteConfirm, setDeleteConfirm] = useState<boolean>(false);
    const deleteConfirmRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<HTMLFormElement>(null);

    const partialSetting: Partial<BuildSettingsKey> = {
        buildId: build.id,
        organizationId: build.companyId,
        companyId: build.companyId,
        dataType: "url",
        guid: v4(),
        key: `buildSettingsKey.${v4()}`
    };

    const [uploadFile] = useUploadImageMutation();

    useOutsideClick(formRef, () => closeEdit());
    useOutsideClick(deleteConfirmRef, () => setDeleteConfirm(false));

    const {
        register,
        handleSubmit,
        control,
        formState: { errors }
        // formState: { errors }
    } = useForm<Partial<CreateImageForm>>({
        defaultValues
    });

    const [uploadAttachments] = useUploadAttachmentsMutation();

    const onSubmit = async (formData: Partial<CreateImageForm>) => {
        if (defaultValues?.key) {
            const patchImageSetting: Partial<BuildSettingsKey> = {
                label: formData.label,
                description: formData.description
            };
            if (build.companyId) {
                if (formData.fileList?.[0]) {
                    const uploadedImage = await uploadFile({ companyId: build.companyId, file: formData.fileList[0] }).unwrap();
                    patchImageSetting.placeholder = uploadedImage && uploadedImage.length > 0 ? uploadedImage[0].url : "";
                    const uploadResp = await uploadAttachments({ companyId: build.companyId, files: [formData.fileList[0]] }).unwrap();
                    const uploadedImageURL = uploadResp?.[0]?.url;
                    if (uploadedImageURL) patchImageSetting.placeholder = uploadedImageURL;
                }
            }
            dispatch(updateBuildSettingsKey({ key: defaultValues.key, update: patchImageSetting }));

            closeEdit();
        } else {
            if (build.companyId && formData.fileList?.[0]) {
                const uploadedImageURL = await uploadFile({ companyId: build.companyId, file: formData.fileList[0] }).unwrap();
                const fullImageSetting: Partial<BuildSettingsKey> = {
                    ...partialSetting,
                    label: formData.label,
                    placeholder: uploadedImageURL && uploadedImageURL.length > 0 ? uploadedImageURL[0].url : "",
                    description: formData.description
                };

                if (fullImageSetting.buildId && fullImageSetting.companyId && fullImageSetting.organizationId) {
                    dispatch(addBuildSettingsKey(fullImageSetting as NewBuildSettingKey));
                    closeEdit();
                }
            }
        }
    };

    const handleDeleteBuildSetting = async () => {
        if (defaultValues?.key) {
            dispatch(deleteBuildSettingsKey(defaultValues.key));
            dispatch(removeBuildSettingKey(defaultValues.key));
            closeEdit();
        }
    };

    return (
        <form className="create-image-setting" ref={formRef} onSubmit={handleSubmit(onSubmit)}>
            <div className="create-is-row">
                <div className="create-is-col">
                    {!defaultValues && (
                        <div className="create-is-group">
                            <OliverText
                                type={TextType.BODY_3}
                                text={t("checkoutFlow.creatingImgSubtitle1") + ` ${build.name}` + t("checkoutFlow.creatingSubtitle2")}
                            />
                        </div>
                    )}
                    <div className="create-is-input-wrapper">
                        <FormInput
                            register={register}
                            name="label"
                            placeholder={t("checkoutFlow.dynTitlePlaceholder")}
                            label={t("checkoutFlow.dynTitle")}
                            validation={{ required: t("checkoutFlow.titleError") }}
                            errors={errors}
                        />
                    </div>
                    <div className="create-is-input-wrapper">
                        <FormInput
                            register={register}
                            name="description"
                            placeholder={t("checkoutFlow.dynDescriptionPlaceholder")}
                            label={t("checkoutFlow.dynDescription")}
                            validation={{ required: t("checkoutFlow.descriptionError") }}
                            errors={errors}
                            textarea
                        />
                    </div>
                    <div className="create-is-input-wrapper">
                        <FormImageInput
                            name="fileList"
                            control={control}
                            defaultValue={defaultValues?.placeholder}
                            rules={{
                                validate: {
                                    fileSize: (files: FileList) => files?.[0]?.size < 1024 * 1024 || t("checkoutFlow.imageSizeError"),
                                    fileType: (files: FileList) => files?.[0]?.type.startsWith("image/") || t("checkoutFlow.imageTypeError")
                                }
                            }}
                        />
                    </div>
                </div>
                {defaultValues &&
                    (deleteConfirm ? (
                        <div className="confirm-delete" ref={deleteConfirmRef}>
                            <OliverText type={TextType.BODY_4} color="red" text={t("checkoutFlow.delete")} />
                            <button type="button" onClick={() => setDeleteConfirm(false)}>
                                <img src="./x-icon-dark.svg" alt="" />
                            </button>
                            <button type="button" onClick={() => handleDeleteBuildSetting()}>
                                <img src="./check.svg" alt="" />
                            </button>
                        </div>
                    ) : (
                        <button type="button" className="create-is-x" onClick={() => setDeleteConfirm(true)}>
                            <img src="./trash.svg" alt="" />
                        </button>
                    ))}
            </div>
            <div className="create-is-row">
                <button className="cancel is-edit-button" type="button" onClick={() => closeEdit()}>
                    <OliverText type={TextType.BODY_2}>{t("checkoutFlow.cancel")}</OliverText>
                </button>
                <button className="submit is-edit-button">
                    <OliverText type={TextType.BODY_2}>{t("checkoutFlow.add")}</OliverText>
                </button>
            </div>
        </form>
    );
};

export default CreateImageSettingForm;
